/**
 * Card Display Configuration
 * 
 * Defines all toggleable card elements, their defaults, and layout rules.
 * This config drives both the Card Fields dropdown and the card rendering logic.
 */

/**
 * Field definitions
 * Each field has:
 * - id: unique identifier (used in settings object)
 * - label: display name in dropdown
 * - defaultEnabled: whether it's on by default
 * - category: grouping in dropdown ('core', 'meta', 'artifacts', 'future')
 * - layout: how it renders ('row', 'inline', 'toolbar')
 * - sharesWith: fields it can share a row with (for responsive layout)
 */
export const CARD_FIELD_DEFINITIONS = [
  // Core fields (always or almost always shown)
  {
    id: 'title',
    label: 'Title',
    defaultEnabled: true,
    alwaysEnabled: true, // Cannot be disabled
    category: 'core',
    layout: 'header',
  },
  {
    id: 'missionIcon',
    label: 'Mission Icon',
    defaultEnabled: true,
    category: 'core',
    layout: 'header',
  },
  
  // Meta fields (status, tags, etc.)
  {
    id: 'statusDropdown',
    label: 'Status',
    defaultEnabled: true,
    category: 'meta',
    layout: 'row',
    sharesWith: ['priorityDropdown', 'difficultyDropdown'],
  },
  {
    id: 'priorityDropdown',
    label: 'Priority',
    defaultEnabled: false,
    category: 'meta',
    layout: 'row',
    sharesWith: ['statusDropdown', 'difficultyDropdown'],
  },
  {
    id: 'difficultyDropdown',
    label: 'Difficulty',
    defaultEnabled: false,
    category: 'meta',
    layout: 'row',
    sharesWith: ['statusDropdown', 'priorityDropdown'],
  },
  {
    id: 'categoryDropdown',
    label: 'Tags',
    defaultEnabled: true,
    category: 'meta',
    layout: 'row',
  },
  {
    id: 'blockedIndicator',
    label: 'Blocked Indicator',
    defaultEnabled: true,
    category: 'meta',
    layout: 'badge',
  },
  // DEPRECATED: Dependencies replaced by Conditions system
  // {
  //   id: 'dependencyIndicator',
  //   label: 'Dependencies',
  //   defaultEnabled: false,
  //   category: 'meta',
  //   layout: 'row',
  // },
  {
    id: 'conditionsIndicator',
    label: 'Conditions',
    defaultEnabled: false,
    category: 'meta',
    layout: 'row',
  },
  {
    id: 'nestedCampaign',
    label: 'Nested Campaign',
    defaultEnabled: true,
    category: 'meta',
    layout: 'row',
  },
  
  // Artifact fields (toolbar buttons)
  {
    id: 'attachmentsButton',
    label: 'Attachments',
    defaultEnabled: true,
    category: 'artifacts',
    layout: 'toolbar',
  },
  {
    id: 'attachedDocs',
    label: 'Attached Docs',
    defaultEnabled: true,
    category: 'artifacts',
    layout: 'row',
  },
  {
    id: 'detailsButton',
    label: 'Open Mission Button',
    defaultEnabled: true,
    category: 'artifacts',
    layout: 'toolbar',
  },
  {
    id: 'archiveButton',
    label: 'Archive Button',
    defaultEnabled: false,
    category: 'artifacts',
    layout: 'toolbar',
  },
  {
    id: 'deleteButton',
    label: 'Delete Button',
    defaultEnabled: true,
    category: 'artifacts',
    layout: 'toolbar',
  },
  {
    id: 'openInViewButton',
    label: 'Open in View',
    defaultEnabled: false,
    category: 'artifacts',
    layout: 'toolbar',
  },
  
  // Date fields
  {
    id: 'dueDate',
    label: 'Due Date',
    defaultEnabled: true,
    category: 'dates',
    layout: 'row',
  },
  {
    id: 'estimatedDuration',
    label: 'Estimated time',
    defaultEnabled: false,
    category: 'dates',
    layout: 'row',
  },
  {
    id: 'createdAt',
    label: 'Created Date',
    defaultEnabled: false,
    category: 'dates',
    layout: 'row',
  },
  {
    id: 'startedAt',
    label: 'Started Date',
    defaultEnabled: false,
    category: 'dates',
    layout: 'row',
  },
  {
    id: 'completedAt',
    label: 'Completed Date',
    defaultEnabled: false,
    category: 'dates',
    layout: 'row',
  },
  {
    id: 'updatedAt',
    label: 'Last Updated',
    defaultEnabled: false,
    category: 'dates',
    layout: 'row',
  },
  
  // Future fields (placeholders for upcoming features)
  // Uncomment these as features are built
  /*
  {
    id: 'priority',
    label: 'Priority',
    defaultEnabled: false,
    category: 'future',
    layout: 'inline',
    sharesWith: ['dueDate'],
  },
  {
    id: 'assignee',
    label: 'Assignee',
    defaultEnabled: false,
    category: 'future',
    layout: 'corner',
  },
  {
    id: 'dateCreated',
    label: 'Date Created',
    defaultEnabled: false,
    category: 'future',
    layout: 'inline',
  },
  {
    id: 'progress',
    label: 'Progress',
    defaultEnabled: false,
    category: 'future',
    layout: 'row',
  },
  */
];

/**
 * Category labels for dropdown grouping
 */
export const FIELD_CATEGORIES = {
  core: 'Core',
  meta: 'Metadata',
  dates: 'Dates',
  artifacts: 'Artifacts',
  future: 'Coming Soon',
};

/**
 * Supported view types for card display settings
 */
export const CARD_DISPLAY_VIEWS = ['flow', 'kanban', 'neural'];

/**
 * Generate default settings object from field definitions
 */
export const getDefaultCardDisplaySettings = () => {
  const settings = {};
  CARD_FIELD_DEFINITIONS.forEach(field => {
    settings[field.id] = field.defaultEnabled;
  });
  return settings;
};

/**
 * Generate default per-view settings object
 * Each view gets its own copy of the default settings
 */
export const getDefaultPerViewCardDisplaySettings = () => {
  const defaults = getDefaultCardDisplaySettings();
  return {
    flow: { ...defaults },
    kanban: { ...defaults },
    neural: { ...defaults },
  };
};

/**
 * Merge saved settings with defaults (handles new fields gracefully)
 * New fields default to their defaultEnabled value
 * 
 * @param {Object} savedSettings - Can be either:
 *   - Legacy format: { fieldId: boolean, ... }
 *   - New format: { flow: { fieldId: boolean, ... }, kanban: { ... } }
 * @param {string} [view] - Optional view to get settings for. If not provided, returns per-view object.
 * @returns {Object} - Settings object (either per-view or for specific view)
 */
const migrateEstimatedDurationField = (settingsObj) => {
  if (!settingsObj || typeof settingsObj !== 'object') return settingsObj;
  const next = { ...settingsObj };
  if (
    Object.prototype.hasOwnProperty.call(next, 'estimatedDays') &&
    !Object.prototype.hasOwnProperty.call(next, 'estimatedDuration')
  ) {
    next.estimatedDuration = next.estimatedDays;
    delete next.estimatedDays;
  }
  // Dropdown uses unified id `estimatedDurationMinutes`; cards persist `estimatedDuration`.
  // Keep a single key so checkmarks and card rendering stay in sync.
  if (Object.prototype.hasOwnProperty.call(next, 'estimatedDurationMinutes')) {
    next.estimatedDuration = next.estimatedDuration === true || next.estimatedDurationMinutes === true;
    delete next.estimatedDurationMinutes;
  }
  return next;
};

export const mergeWithDefaults = (savedSettings, view) => {
  const defaults = getDefaultCardDisplaySettings();

  // No saved settings - return defaults
  if (!savedSettings) {
    if (view) return { ...defaults };
    return getDefaultPerViewCardDisplaySettings();
  }

  // Check if it's the new per-view format (has 'flow', 'kanban', or 'neural' keys)
  const isPerViewFormat = savedSettings.flow !== undefined
    || savedSettings.kanban !== undefined
    || savedSettings.neural !== undefined;

  if (isPerViewFormat) {
    const result = {
      flow: migrateEstimatedDurationField({ ...defaults, ...(savedSettings.flow || {}) }),
      kanban: migrateEstimatedDurationField({ ...defaults, ...(savedSettings.kanban || {}) }),
      neural: migrateEstimatedDurationField({ ...defaults, ...(savedSettings.neural || {}) }),
    };
    return view ? result[view] : result;
  }

  const mergedLegacy = migrateEstimatedDurationField({ ...defaults, ...savedSettings });
  if (view) return mergedLegacy;

  return {
    flow: { ...mergedLegacy },
    kanban: { ...mergedLegacy },
    neural: { ...mergedLegacy },
  };
};

/**
 * Check if settings are in legacy (shared) format
 */
export const isLegacyCardDisplaySettings = (settings) => {
  if (!settings) return false;
  return settings.flow === undefined
    && settings.kanban === undefined
    && settings.neural === undefined;
};

/**
 * Get visible fields grouped by category
 */
export const getFieldsByCategory = () => {
  const grouped = {};
  CARD_FIELD_DEFINITIONS.forEach(field => {
    if (!grouped[field.category]) {
      grouped[field.category] = [];
    }
    grouped[field.category].push(field);
  });
  return grouped;
};

/**
 * Check if a field should share a row with another enabled field
 */
export const shouldShareRow = (fieldId, settings) => {
  const field = CARD_FIELD_DEFINITIONS.find(f => f.id === fieldId);
  if (!field || !field.sharesWith) return false;
  
  return field.sharesWith.some(otherId => settings[otherId]);
};

export default {
  CARD_FIELD_DEFINITIONS,
  FIELD_CATEGORIES,
  CARD_DISPLAY_VIEWS,
  getDefaultCardDisplaySettings,
  getDefaultPerViewCardDisplaySettings,
  mergeWithDefaults,
  getFieldsByCategory,
  shouldShareRow,
  isLegacyCardDisplaySettings,
};

